import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatError } from "@/lib/api";
import { fmtDate, money, usePlan } from "@/lib/plan";

const STATUS = { paid: "Paid", refund_requested: "Refund requested", refunded: "Refunded", failed: "Failed" };

function RefundForm({ p, onDone }) {
  const [reason, setReason] = useState("");
  const send = async () => {
    try { toast.success((await api.post(`/billing/payments/${p.order_id}/refund-request`, { reason })).data.message); onDone(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="mt-2 flex flex-col gap-2">
      <Textarea data-testid={`refund-reason-${p.order_id}`} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Tell us why you'd like a refund" />
      <Button size="sm" data-testid={`refund-submit-${p.order_id}`} disabled={reason.trim().length < 5} onClick={send} className="self-start rounded-full">Request refund</Button>
    </div>
  );
}

export function BillingSection() {
  const { plan } = usePlan();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/billing/history").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  if (!plan?.limits) return null;
  return (
    <div data-testid="billing-section" className="flex flex-col gap-3 rounded-xl border border-line p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-ink">Plan & billing: <span data-testid="billing-plan-name">{plan.plan_name}</span></p>
          <p className="text-xs text-ink-3">{plan.expires_at ? `Active until ${fmtDate(plan.expires_at)}. Payments are one-time and never renew automatically, so there's nothing to cancel.` : "No end date."}</p>
        </div>
        <Link to="/pricing" data-testid="billing-manage-link" className="text-sm font-semibold text-aqua hover:underline">{plan.plan_id === "free" ? "Upgrade" : "Renew or change"}</Link>
      </div>
      {rows.length > 0 && (
        <ul className="divide-y divide-line text-sm">
          {rows.map((p) => (
            <li key={p.order_id} data-testid={`billing-row-${p.order_id}`} className="py-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className={p.kind === "topup" ? "" : "capitalize"}>{p.kind === "topup" ? `${p.requests} extra AI requests` : `${p.plan_id} · ${p.cycle}`} · {fmtDate(p.created_at)}</span>
                <span className="font-mono">{money(p.amount, p.currency || "INR")} · {STATUS[p.status] || p.status}</span>
              </div>
              {p.refundable && open !== p.order_id && <button data-testid={`refund-open-${p.order_id}`} onClick={() => setOpen(p.order_id)} className="text-xs text-ink-3 underline">Request a refund (within 7 days)</button>}
              {open === p.order_id && <RefundForm p={p} onDone={() => { setOpen(null); load(); }} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
